import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { GraphQLService } from '../../core/services/graphql.service';

export interface LoginResponse {
  login: {
    __typename: string;
    id?: string;
    identifier?: string;
    channels?: Array<{
      id: string;
      code: string;
      token: string;
      permissions: string[];
    }>;
    errorCode?: string;
    message?: string;
  };
}

export interface CurrentUser {
  me: {
    id: string;
    identifier: string;
    channels: Array<{
      id: string;
      code: string;
      token: string;
      permissions: string[];
    }>;
  };
}

@Injectable({
  providedIn: 'root',
})
export class VendureAuthService {
  constructor(private graphql: GraphQLService) {}

  /**
   * Login with cookie-based session authentication
   */
  async login(
    username: string,
    password: string,
    rememberMe: boolean
  ): Promise<string | null> {
    const mutation = `
      mutation AttemptLogin(
        $username: String!
        $password: String!
        $rememberMe: Boolean!
      ) {
        login(
          username: $username
          password: $password
          rememberMe: $rememberMe
        ) {
          ... on CurrentUser {
            id
            identifier
            channels {
              id
              code
              token
              permissions
            }
          }
          ... on ErrorResult {
            errorCode
            message
          }
          __typename
        }
      }
    `;

    const variables = {
      username,
      password,
      rememberMe,
    };

    try {
      const response = await firstValueFrom(
        this.graphql.request<LoginResponse>(mutation, variables)
      );

      // console.log('Vendure login response:', response);

      if (response.login.__typename === 'CurrentUser') {
        // Extract the token from the first channel (optional - for additional auth)
        const token = response.login.channels?.[0]?.token;
        if (token) {
          localStorage.setItem('vendureToken', token);
        }

        // The real authentication is now cookie-based
        // console.log('Login successful - session cookie set');
        return token || 'cookie-authenticated';
      } else {
        console.error('Vendure login error:', response.login.message);
      }
    } catch (err) {
      console.error('GraphQL request error:', err);
    }

    return null;
  }

  /**
   * Check if user is currently authenticated
   */
  async checkAuth(): Promise<boolean> {
    const query = `
      query GetCurrentUser {
        me {
          id
          identifier
          channels {
            id
            code
            token
            permissions
          }
        }
      }
    `;

    try {
      const response = await firstValueFrom(
        this.graphql.request<CurrentUser>(query)
      );

      // console.log('Current user:', response);
      return !!response.me;
    } catch (err) {
      console.error('Auth check failed:', err);
      return false;
    }
  }

  /**
   * Logout (invalidate session)
   */
  async logout(): Promise<boolean> {
    const mutation = `
      mutation Logout {
        logout {
          success
        }
      }
    `;

    try {
      const response = await firstValueFrom(
        this.graphql.request<{ logout: { success: boolean } }>(mutation)
      );

      // Clear local storage
      localStorage.removeItem('vendureToken');

      return response.logout.success;
    } catch (err) {
      console.error('Logout error:', err);
      // Clear local storage even if request fails
      localStorage.removeItem('vendureToken');
      return false;
    }
  }

  getToken(): string | null {
    return localStorage.getItem('vendureToken');
  }
}
